import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import '../styles/nassar-theme.css';
import './Users.css';

const Users = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        name: '',
        phone_num: '',
        password: ''
    });
    const [formData, setFormData] = useState({
        name: '',
        password: '',
        phone_num: '',
        type: 'Employee'
    });

    useEffect(() => {
        // Check user permissions first
        const token = localStorage.getItem('nassar_token');
        if (token) {
            try {
                const payload = token.split('.')[1];
                const decodedPayload = JSON.parse(atob(payload));
                
                // If user is not Admin, redirect to clearance files
                if (decodedPayload.type !== 'Admin') {
                    navigate('/dashboard/clearance-files', { replace: true });
                    return;
                }
            } catch (error) {
                console.error('Error decoding token:', error);
                navigate('/dashboard/clearance-files', { replace: true });
                return;
            }
        }
        
        fetchUsers();
    }, [navigate]);

    useEffect(() => {
        // Filter users based on search term
        if (searchTerm.trim() === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user => 
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.phone_num.includes(searchTerm) ||
                (user.type === 'Admin' ? 'مدير' : 'موظف').includes(searchTerm)
            );
            setFilteredUsers(filtered);
        }
    }, [searchTerm, users]);

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            // TODO: Replace with actual API endpoint
            const response = await axiosInstance.get('/users/');
            setUsers(response.data);
            setFilteredUsers(response.data);
            setError('');
        } catch (err) {
            setError('فشل في تحميل المستخدمين');
            console.error('Error fetching users:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = () => {
        setFormData({
            name: '',
            password: '',
            phone_num: '',
            type: 'Employee'
        });
        setValidationErrors({
            name: '',
            phone_num: '',
            password: ''
        });
        setShowAddModal(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            password: '',
            phone_num: user.phone_num,
            type: user.type
        });
        setValidationErrors({
            name: '',
            phone_num: '',
            password: ''
        });
        setShowEditModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear validation error for this field when user types
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {
            name: '',
            phone_num: '',
            password: ''
        };
        let isValid = true;

        // Validate name
        if (!formData.name.trim()) {
            errors.name = 'الاسم مطلوب';
            isValid = false;
        } else {
            // Check for duplicate name (excluding current user when editing)
            const duplicateName = users.find(user => 
                user.name.toLowerCase() === formData.name.trim().toLowerCase() &&
                (!editingUser || user.id !== editingUser.id)
            );
            if (duplicateName) {
                errors.name = 'هذا الاسم موجود مسبقاً';
                isValid = false;
            }
        }

        // Validate phone number
        if (!formData.phone_num.trim()) {
            errors.phone_num = 'رقم الهاتف مطلوب';
            isValid = false;
        } else {
            // Check for duplicate phone number (excluding current user when editing)
            const duplicatePhone = users.find(user => 
                user.phone_num === formData.phone_num.trim() &&
                (!editingUser || user.id !== editingUser.id)
            );
            if (duplicatePhone) {
                errors.phone_num = 'رقم الهاتف مسجل مسبقاً';
                isValid = false;
            }
        }

        // Validate password (only required when adding new user)
        if (!editingUser && !formData.password.trim()) {
            errors.password = 'كلمة المرور مطلوبة';
            isValid = false;
        }

        setValidationErrors(errors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form before submission
        if (!validateForm()) {
            return;
        }
        
        try {
            if (editingUser) {
                // Update existing user
                const updateData = {
                    name: formData.name.trim(),
                    phone_num: formData.phone_num.trim()
                };
                
                // Only include password if it's not empty
                if (formData.password) {
                    updateData.password = formData.password;
                }

                await axiosInstance.put(`/users/${editingUser.id}/`, updateData);
                
                // Show success modal instead of alert
                setShowEditModal(false);
                setShowSuccessModal(true);
            } else {
                // Create new user
                const createData = {
                    name: formData.name.trim(),
                    password: formData.password,
                    phone_num: formData.phone_num.trim(),
                    type: 'Employee' // Always Employee by default
                };

                await axiosInstance.post('/users/', createData);
                setShowAddModal(false);
            }
            
            // Refresh users list
            await fetchUsers();
            
            // Close modal and reset form
            setEditingUser(null);
            setFormData({
                name: '',
                password: '',
                phone_num: '',
                type: 'Employee'
            });
            setValidationErrors({
                name: '',
                phone_num: '',
                password: ''
            });
        } catch (err) {
            console.error('Error saving user:', err);
            const errorMsg = err.response?.data?.detail || 
                           err.response?.data?.error || 
                           (editingUser ? 'فشل في تحديث المستخدم' : 'فشل في إضافة المستخدم');
            alert(errorMsg);
        }
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setShowAddModal(false);
        setEditingUser(null);
        setFormData({
            name: '',
            password: '',
            phone_num: '',
            type: 'Employee'
        });
        setValidationErrors({
            name: '',
            phone_num: '',
            password: ''
        });
    };

    const handleDelete = async (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        
        try {
            await axiosInstance.delete(`/users/${userToDelete.id}/`);
            await fetchUsers();
            setShowDeleteModal(false);
            setUserToDelete(null);
        } catch (err) {
            console.error('Error deleting user:', err);
            const errorMsg = err.response?.data?.detail || 
                           err.response?.data?.error || 
                           'فشل في حذف المستخدم';
            alert(errorMsg);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>جاري تحميل المستخدمين...</p>
            </div>
        );
    }

    return (
        <div className="users-page">
            {error && (
                <div className="card" style={{ backgroundColor: '#fee', borderColor: '#fcc' }}>
                    <p style={{ color: '#c33', margin: 0 }}>{error}</p>
                </div>
            )}

            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">
                        <svg style={{ width: '24px', height: '24px', marginLeft: '0.5rem', display: 'inline-block' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                        </svg>
                        إدارة المستخدمين
                    </h3>
                    <button onClick={handleAdd} className="btn btn-primary">
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                        </svg>
                        <span>إضافة موظف</span>
                    </button>
                </div>

                {/* Search Box */}
                <div style={{ padding: '1.5rem' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <svg 
                            style={{ 
                                position: 'absolute', 
                                right: '1rem', 
                                top: '50%', 
                                transform: 'translateY(-50%)',
                                width: '20px',
                                height: '20px',
                                color: 'var(--text-light)'
                            }} 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                        >
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="ابحث عن موظف (الاسم، رقم الهاتف)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ paddingRight: '3rem' }}
                        />
                    </div>
                </div>

                {filteredUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light)' }}>
                        <svg style={{ width: '64px', height: '64px', margin: '0 auto 1rem', opacity: 0.3 }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                        </svg>
                        <p>{searchTerm ? 'لا توجد نتائج للبحث' : 'لا يوجد مستخدمين'}</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>الاسم</th>
                                    <th>رقم الهاتف</th>
                                    <th>النوع</th>
                                    <th>الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td>{user.name}</td>
                                        <td>{user.phone_num}</td>
                                        <td>
                                            <span style={{
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '12px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                backgroundColor: user.type === 'Admin' ? '#e3f2fd' : '#fff3cd',
                                                color: user.type === 'Admin' ? '#1565c0' : '#856404'
                                            }}>
                                                {user.type === 'Admin' ? 'مدير' : 'موظف'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                <button 
                                                    onClick={() => handleEdit(user)}
                                                    className="btn btn-primary"
                                                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                                                >
                                                    <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                    </svg>
                                                    <span>تعديل</span>
                                                </button>
                                                
                                                {user.type === 'Employee' && (
                                                    <button 
                                                        onClick={() => handleDelete(user)}
                                                        className="btn"
                                                        style={{ 
                                                            padding: '0.5rem 1rem', 
                                                            fontSize: '0.9rem',
                                                            backgroundColor: '#dc3545',
                                                            color: 'white',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                        <span>حذف</span>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="modal-overlay">
                    <div className="modal modal-medium">
                        <div className="modal-header">
                            <h3 className="modal-title">تعديل بيانات المستخدم</h3>
                            <button onClick={handleCloseModal} className="modal-close">
                                <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '24px', height: '24px' }}>
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">الاسم</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className={`form-input ${validationErrors.name ? 'error' : ''}`}
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        style={{
                                            borderColor: validationErrors.name ? '#dc3545' : undefined
                                        }}
                                    />
                                    {validationErrors.name && (
                                        <div style={{
                                            color: '#dc3545',
                                            fontSize: '0.875rem',
                                            marginTop: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span>{validationErrors.name}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">كلمة المرور الجديدة (اتركه فارغاً للإبقاء على كلمة المرور الحالية)</label>
                                    <input
                                        type="password"
                                        name="password"
                                        className="form-input"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="كلمة المرور الجديدة (اختياري)"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">رقم الهاتف</label>
                                    <input
                                        type="text"
                                        name="phone_num"
                                        className={`form-input ${validationErrors.phone_num ? 'error' : ''}`}
                                        value={formData.phone_num}
                                        onChange={handleInputChange}
                                        style={{
                                            borderColor: validationErrors.phone_num ? '#dc3545' : undefined
                                        }}
                                    />
                                    {validationErrors.phone_num && (
                                        <div style={{
                                            color: '#dc3545',
                                            fontSize: '0.875rem',
                                            marginTop: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span>{validationErrors.phone_num}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">النوع</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.type === 'Admin' ? 'مدير' : 'موظف'}
                                        disabled
                                        style={{ 
                                            backgroundColor: '#f5f5f5',
                                            cursor: 'not-allowed',
                                            color: '#666'
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="submit" className="btn btn-primary">
                                    <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>حفظ</span>
                                </button>
                                <button type="button" onClick={handleCloseModal} className="btn btn-outline">
                                    <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    <span>إلغاء</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Modal */}
            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal modal-medium">
                        <div className="modal-header">
                            <h3 className="modal-title">إضافة موظف جديد</h3>
                            <button onClick={handleCloseModal} className="modal-close">
                                <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '24px', height: '24px' }}>
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">الاسم</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className={`form-input ${validationErrors.name ? 'error' : ''}`}
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="أدخل اسم الموظف"
                                        style={{
                                            borderColor: validationErrors.name ? '#dc3545' : undefined
                                        }}
                                    />
                                    {validationErrors.name && (
                                        <div style={{
                                            color: '#dc3545',
                                            fontSize: '0.875rem',
                                            marginTop: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span>{validationErrors.name}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">كلمة المرور</label>
                                    <input
                                        type="password"
                                        name="password"
                                        className={`form-input ${validationErrors.password ? 'error' : ''}`}
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="أدخل كلمة المرور"
                                        style={{
                                            borderColor: validationErrors.password ? '#dc3545' : undefined
                                        }}
                                    />
                                    {validationErrors.password && (
                                        <div style={{
                                            color: '#dc3545',
                                            fontSize: '0.875rem',
                                            marginTop: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span>{validationErrors.password}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">رقم الهاتف</label>
                                    <input
                                        type="text"
                                        name="phone_num"
                                        className={`form-input ${validationErrors.phone_num ? 'error' : ''}`}
                                        value={formData.phone_num}
                                        onChange={handleInputChange}
                                        placeholder="أدخل رقم الهاتف"
                                        style={{
                                            borderColor: validationErrors.phone_num ? '#dc3545' : undefined
                                        }}
                                    />
                                    {validationErrors.phone_num && (
                                        <div style={{
                                            color: '#dc3545',
                                            fontSize: '0.875rem',
                                            marginTop: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span>{validationErrors.phone_num}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="submit" className="btn btn-primary">
                                    <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                                    </svg>
                                    <span>إضافة</span>
                                </button>
                                <button type="button" onClick={handleCloseModal} className="btn btn-outline">
                                    <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    <span>إلغاء</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && userToDelete && (
                <div className="modal-overlay">
                    <div className="modal modal-medium">
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' }}>
                            <h3 className="modal-title">تأكيد الحذف</h3>
                            <button onClick={cancelDelete} className="modal-close">
                                <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '24px', height: '24px' }}>
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fee',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.5rem',
                                    border: '3px solid #dc3545'
                                }}>
                                    <svg style={{ width: '40px', height: '40px', color: '#dc3545' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 style={{ 
                                    fontSize: '1.5rem', 
                                    fontWeight: '700', 
                                    color: 'var(--text-dark)', 
                                    marginBottom: '1rem' 
                                }}>
                                    هل أنت متأكد؟
                                </h3>
                                <p style={{ 
                                    fontSize: '1rem', 
                                    color: 'var(--text-light)', 
                                    marginBottom: '0.5rem',
                                    lineHeight: '1.6'
                                }}>
                                    سيتم حذف الموظف
                                </p>
                                <p style={{ 
                                    fontSize: '1.25rem', 
                                    fontWeight: '700', 
                                    color: 'var(--primary-color)',
                                    marginBottom: '1rem'
                                }}>
                                    "{userToDelete.name}"
                                </p>
                                <p style={{ 
                                    fontSize: '0.95rem', 
                                    color: '#dc3545',
                                    fontWeight: '600'
                                }}>
                                    هذا الإجراء لا يمكن التراجع عنه!
                                </p>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                onClick={confirmDelete}
                                className="btn"
                                style={{ 
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none'
                                }}
                            >
                                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>نعم، احذف</span>
                            </button>
                            <button 
                                onClick={cancelDelete}
                                className="btn btn-outline"
                            >
                                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span>إلغاء</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal - Update Notification */}
            {showSuccessModal && (
                <div className="modal-overlay">
                    <div className="modal modal-medium">
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #0e2a59 0%, #03428d 100%)' }}>
                            <h3 className="modal-title">تم التحديث بنجاح</h3>
                            <button onClick={() => setShowSuccessModal(false)} className="modal-close">
                                <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '24px', height: '24px' }}>
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                                <div style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    backgroundColor: '#e3f2fd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.5rem',
                                    border: '4px solid #03428d',
                                    animation: 'scaleIn 0.3s ease-out'
                                }}>
                                    <svg style={{ width: '50px', height: '50px', color: '#03428d' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                
                                <h3 style={{ 
                                    fontSize: '1.75rem', 
                                    fontWeight: '700', 
                                    color: 'var(--text-dark)', 
                                    marginBottom: '1rem' 
                                }}>
                                    تم تحديث بيانات المستخدم بنجاح!
                                </h3>
                                
                                <div style={{
                                    backgroundColor: '#fff3cd',
                                    border: '2px solid #ffc107',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    marginTop: '1.5rem',
                                    textAlign: 'right'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                        <svg style={{ width: '28px', height: '28px', color: '#856404', flexShrink: 0, marginTop: '2px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ 
                                                fontSize: '1.1rem', 
                                                fontWeight: '700', 
                                                color: '#856404', 
                                                marginBottom: '0.5rem',
                                                marginTop: 0
                                            }}>
                                                ملاحظة هامة
                                            </h4>
                                            <p style={{ 
                                                fontSize: '1rem', 
                                                color: '#856404', 
                                                lineHeight: '1.8',
                                                margin: 0
                                            }}>
                                                إذا كان المستخدم مسجل دخول حالياً، يجب عليه تسجيل الخروج وإعادة تسجيل الدخول حتى تظهر التعديلات.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer" style={{ justifyContent: 'center' }}>
                            <button 
                                onClick={() => setShowSuccessModal(false)}
                                className="btn btn-primary"
                                style={{ minWidth: '150px' }}
                            >
                                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span>فهمت</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;

